import { createSlice } from '@reduxjs/toolkit';

import { getUserURL } from '../api/commonHTTP';
import { DEFAULT_PAGE_LOAD_SIZE } from '../api/pagination';

import { apiCallBegan, apiVersion } from './api';

const accessToken = localStorage.getItem('accessToken');

const slice = createSlice({
    name: 'userService',
    initialState: {
        loggedInUser: {
            // List of roles this user can have. Hard-code initial values of these
            // for testing until user can log in.
            //
            // When the user can log in this will be filled with the users roles etc.
            //
            // Roles are defined in UserRoles
            roles: [], // this gets sent from the payload during login
            // Set this when there is a user logged in
            userName: '', // this gets sent from the payload during login
            firstName: '', // this gets sent from the payload during login
            lastName: '', // this gets sent from the payload during login
            currentParticipant: {
                id: '',
                name: ''
            },
            // Index to current environment.

            environment: import.meta.env.VITE_APP_ENVIRONMENT
        },
        loggedInUserDetails: {},
        accessToken: accessToken ? accessToken : '',
        idToken: '',
        token: '',
        currentTeam: {},
        allTeams: [],
        teams: [],
        teamsForLoggedInUser: [],
        teamsForService: [],
        otherTeamsForService: [],
        userTypes: [],
        currentUser: {},
        users: [],
        usersMetaData: {},
        usersById: {},
        usersByLoggedInUserServiceIds: [],
        usersByLoggedInUserServiceIdsMetaData: {},
        usersBySearch: [],
        usersBySearchMetaData: {},
        usersForComms: [],
        usersForForms: [],
        usersForPools: [],
        usersForVacancies: [],
        usersForVacanciesMetaData: {},
        usersMinimalDetails: [],
        loadingTeamsForService: false,
        loadingUsers: false,
        loadingUsersByLoggedInUserServiceIds: false,
        loadingUsersForVacancies: false,
        successMessage: '',
        errorMessage: '',
        businessLogicError: {},
        version: {}
    },
    reducers: {
        loginSuccess: (state, action) => {
            const { accessToken, refreshToken, ...rest } = action.payload;
            state.errorMessage = '';
            state.accessToken = accessToken;
            state.refreshToken = refreshToken;
            state.loggedInUser = { ...state.loggedInUser, ...rest };
        },

        logoutSuccess: (state) => {
            state.accessToken = '';
            state.idToken = '';
            state.refreshToken = '';
            state.loggedInUser = { environment: import.meta.env.VITE_APP_ENVIRONMENT };
        },

        teamCreated: (state, action) => {
            state.currentTeam = action.payload;
        },

        teamLoaded: (state, action) => {
            state.currentTeam = action.payload;
            state.allTeams = [
                ...state.allTeams.filter((el) => el.id !== action.payload.id),
                action.payload
            ];
        },

        allTeamsLoaded: (state, action) => {
            state.allTeams = action.payload.content;
        },

        teamsForLoggedInUserLoaded: (state, action) => {
            state.teamsForLoggedInUser = action.payload;
        },

        otherTeamsForServiceLoaded: (state, action) => {
            state.otherTeamsForService = action.payload;
        },
        clearOtherTeamsForService: (state) => {
            state.otherTeamsForService = [];
        },

        teamsForServiceLoaded: (state, action) => {
            state.loadingTeamsForService = false;
            state.teamsForService = action.payload;
        },

        teamsForServiceLoading: (state) => {
            state.loadingTeamsForService = true;
        },

        teamsForServiceLoadingFailure: (state, action) => {
            state.errorMessage = action.payload;
            state.loadingTeamsForService = false;
        },

        clearTeamsForService: (state) => {
            state.teamsForService = [];
        },

        loggedInUserLoaded: (state, action) => {
            state.loggedInUserDetails = action.payload;
        },

        userCreated: (state, action) => {
            state.currentUser = action.payload;
        },

        userUpdated: (state, action) => {
            const filteredUsers = state.users.filter((el) => el.id !== action.payload.id);
            state.users = [...filteredUsers, action.payload];
        },

        userLoaded: (state, action) => {
            state.currentUser = action.payload;
            const users = state.users.filter((el) => el.id !== action.payload.id);
            state.users = [...users, action.payload];
        },

        userByIdLoaded: (state, action) => {
            state.usersById[action.payload.id] = action.payload;
        },

        usersByLoggedInUserServiceIdsLoading: (state) => {
            state.loadingUsersByLoggedInUserServiceIds = true;
        },
        usersByLoggedInUserServiceIdsLoaded: (state, action) => {
            state.loadingUsersByLoggedInUserServiceIds = false;
            const { content, ...rest } = action.payload;
            if (rest.first) {
                state.usersByLoggedInUserServiceIds = content;
                state.usersForComms = content;
                state.usersForForms = content;
                state.usersForPools = content;
                state.usersForVacancies = content;
            } else {
                state.usersByLoggedInUserServiceIds = [
                    ...state.usersByLoggedInUserServiceIds,
                    ...content
                ];
                state.usersForComms = [...state.usersForComms, ...content];
                state.usersForForms = [...state.usersForForms, ...content];
                state.usersForPools = [...state.usersForPools, ...content];
                state.usersForVacancies = [...state.usersForVacancies, ...content];
            }
            state.usersByLoggedInUserServiceIdsMetaData = rest;
        },

        usersBySearchLoaded: (state, action) => {
            const { content, ...rest } = action.payload;
            state.usersBySearch = rest.first ? content : [...state.usersBySearch, ...content];
            state.usersBySearchMetaData = rest;
        },
        clearUsersBySearch: (state) => {
            state.usersBySearch = [];
            state.usersBySearchMetaData = {};
        },

        usersLoading: (state) => {
            state.loadingUsers = true;
        },

        usersLoaded: (state, action) => {
            state.loadingUsers = false;
            if ('content' in action.payload) {
                const { content, ...rest } = action.payload;
                state.users = rest.first ? content : [...state.users, ...content];
                state.usersMetaData = rest;
            } else state.users = action.payload;
        },

        usersMinimalDetailsLoaded: (state, action) => {
            state.usersMinimalDetails = action.payload;
        },
        clearUsersMinimalDetails: (state) => {
            state.usersMinimalDetails = [];
        },

        usersForCommsLoaded: (state, action) => {
            state.usersForComms =
                'content' in action.payload ? action.payload.content : action.payload;
        },

        usersForFormsLoaded: (state, action) => {
            state.usersForForms =
                'content' in action.payload ? action.payload.content : action.payload;
        },

        userForFormsLoaded: (state, action) => {
            const usersForForms = state.usersForForms.filter((el) => el.id !== action.payload.id);
            state.usersForForms = [...usersForForms, action.payload];
        },

        usersForVacanciesLoading: (state) => {
            state.loadingUsersForVacancies = true;
        },
        usersForVacanciesLoaded: (state, action) => {
            state.loadingUsersForVacancies = false;
            const { content, ...rest } = action.payload;
            // There will already be entries from the loggedInUsers ServiceIds
            state.usersForVacancies = [...state.usersForVacancies, ...content];
            state.usersForVacanciesMetaData = rest;
        },

        userTypesLoaded: (state, action) => {
            state.userTypes = action.payload;
        },

        loggedInUserSet: (state, action) => {
            state.loggedInUser = action.payload;
        },

        userRolesSet: (state, action) => {
            state.loggedInUser.roles = action.payload;
        },

        successMessageSet: (state, action) => {
            state.successMessage = action.payload;
            state.errorMessage = '';
        },

        errorOccurred: (state, action) => {
            state.errorMessage = action.payload;
        },

        businessLogicErrorOccurred: (state, action) => {
            state.businessLogicError = Object.entries(action.error).reduce(
                (acc, [key, value]) => ({
                    ...acc,
                    [key]: { error: true, message: value }
                }),
                {}
            );
        },

        clearBusinessLogicError: (state) => {
            state.businessLogicError = {};
        },

        idTokenSet: (state, action) => {
            state.idToken = action.payload;
        },

        versionLoaded: (state, action) => {
            state.version = action.payload;
        }
    }
});

// ActionCreators. These are commands for things which are about to happen
// so 'loadTeams' (command) rather than 'teamsLoaded' (event that has happened)

export const login = (data) =>
    apiCallBegan({
        url: import.meta.env.VITE_APP_USER_SERVICE_URL + '/rest/login',
        method: 'post',
        data,
        onSuccess: loginSuccess.type,
        onError: errorOccurred.type
    });

export const loadUserAttributes = () =>
    apiCallBegan({
        url: import.meta.env.VITE_APP_USER_SERVICE_URL + '/rest/user-attributes',
        onSuccess: loginSuccess.type,
        onError: errorOccurred.type
    });

export const logOut = (data) =>
    apiCallBegan({
        url: import.meta.env.VITE_APP_USER_SERVICE_URL + '/rest/logOff',
        method: 'post',
        data,
        onSuccess: logoutSuccess().type,
        onError: errorOccurred.type
    });

export const createTeam = (name, data) =>
    apiCallBegan({
        url: getUserURL() + 'rest/team',
        method: 'post',
        data,
        onSuccess: teamCreated.type,
        successMessage: `Created team ${name}`,
        onError: errorOccurred.type
    });

export const loadAllTeams = () =>
    apiCallBegan({
        url: getUserURL() + 'rest/team',
        onSuccess: allTeamsLoaded.type,
        onError: errorOccurred.type
    });

export const loadTeam = (id) =>
    apiCallBegan({
        url: getUserURL() + `rest/team/${id}`,
        onSuccess: teamLoaded.type,
        onError: errorOccurred.type
    });

export const searchTeamsForLoggedInUser = (data) =>
    apiCallBegan({
        url: getUserURL() + 'rest/team/search',
        data,
        method: 'post',
        onSuccess: teamsForLoggedInUserLoaded.type,
        onError: errorOccurred.type
    });

export const loadTeamsForService = (data) =>
    apiCallBegan({
        url: getUserURL() + 'rest/team/search',
        data,
        method: 'post',
        onStart: teamsForServiceLoading.type,
        onSuccess: teamsForServiceLoaded.type,
        onError: teamsForServiceLoadingFailure.type
    });

export const loadOtherTeamsForService = (data) =>
    apiCallBegan({
        url: getUserURL() + 'rest/team/search',
        data,
        method: 'post',
        onSuccess: otherTeamsForServiceLoaded.type,
        onError: errorOccurred.type
    });

export const createUser = (data) =>
    apiCallBegan({
        url: getUserURL() + 'rest/user',
        method: 'post',
        data,
        onSuccess: userCreated.type,
        successMessage: `Created user for email address ${data.loginEmailAddress}`,
        onError: errorOccurred.type
    });

export const updateUser = (data) =>
    apiCallBegan({
        url: getUserURL() + `rest/user/${data.id}`,
        method: 'put',
        data,
        onSuccess: userUpdated.type,
        successMessage: `Updated user ${data.loginEmailAddress}`,
        onError: errorOccurred.type
    });

export const loadUser = (userId) =>
    apiCallBegan({
        url: getUserURL() + `rest/user/${userId}`,
        onSuccess: userLoaded.type,
        onError: errorOccurred.type
    });

export const loadLoggedInUser = (userId) =>
    apiCallBegan({
        url: getUserURL() + `rest/user/${userId}`,
        onSuccess: loggedInUserLoaded.type,
        onError: errorOccurred.type
    });

export const loadUserById = (userId) =>
    apiCallBegan({
        url: getUserURL() + `rest/user/${userId}`,
        onSuccess: userByIdLoaded.type,
        onError: errorOccurred.type
    });

export const loadUsersForComms = (serviceIds) =>
    apiCallBegan({
        url: getUserURL() + 'rest/user/search',
        method: 'post',
        data: { serviceIds },
        onSuccess: usersForCommsLoaded.type,
        successMessage: `Users loaded`,
        onError: errorOccurred.type
    });

export const loadUserForForms = (userId) =>
    apiCallBegan({
        url: getUserURL() + `rest/user/${userId}`,
        onSuccess: userForFormsLoaded.type,
        successMessage: `Users for forms loaded`,
        onError: errorOccurred.type
    });

export const loadUsersForForms = (serviceIds) =>
    apiCallBegan({
        url: getUserURL() + 'rest/user/search',
        method: 'post',
        data: { serviceIds },
        onSuccess: usersForFormsLoaded.type,
        successMessage: `Users for forms loaded`,
        onError: errorOccurred.type
    });

export const searchUsersByLoggedInUserServiceIds = (
    serviceIds,
    page = 0,
    size = DEFAULT_PAGE_LOAD_SIZE
) =>
    apiCallBegan({
        url: getUserURL() + `rest/user/search?page=${page}&size=${size}`,
        method: 'post',
        data: { serviceIds },
        onStart: usersByLoggedInUserServiceIdsLoading.type,
        onSuccess: usersByLoggedInUserServiceIdsLoaded.type,
        successMessage: `Users loaded`,
        onError: errorOccurred.type
    });

export const searchUsersForVacancies = (data, page = 0, size = DEFAULT_PAGE_LOAD_SIZE) =>
    apiCallBegan({
        url: getUserURL() + `rest/user/search?page=${page}&size=${size}`,
        method: 'post',
        data,
        onStart: usersForVacanciesLoading.type,
        onSuccess: usersForVacanciesLoaded.type,
        successMessage: `Users for vacancies loaded`,
        onError: errorOccurred.type
    });

export const loadUsersByServiceIds = (serviceIds, page = 0, size = DEFAULT_PAGE_LOAD_SIZE) =>
    apiCallBegan({
        url: getUserURL() + `rest/user/search?page=${page}&size=${size}`,
        method: 'post',
        data: { serviceIds },
        onSuccess: usersLoaded.type,
        successMessage: `Users loaded`,
        onError: errorOccurred.type
    });

export const loadUsersByUserIds = (userIds, page = 0, size = DEFAULT_PAGE_LOAD_SIZE) =>
    apiCallBegan({
        url: getUserURL() + `rest/user/search?page=${page}&size=${size}`,
        method: 'post',
        data: { userIds },
        onStart: usersLoading.type,
        onSuccess: usersLoaded.type,
        successMessage: `Users loaded`,
        onError: errorOccurred.type
    });

export const loadUsersBySearch = (data, page = 0, size = DEFAULT_PAGE_LOAD_SIZE) =>
    apiCallBegan({
        url: getUserURL() + `rest/user/search?page=${page}&size=${size}`,
        method: 'post',
        data,
        onSuccess: usersBySearchLoaded.type,
        successMessage: `Users loaded`,
        onError: errorOccurred.type
    });

export const loadUsersBySimpleSearch = (term, page = 0, size = DEFAULT_PAGE_LOAD_SIZE) =>
    apiCallBegan({
        url: getUserURL() + `rest/user/simple-search/${term}?page=${page}&size=${size}`,
        method: 'get',
        onSuccess: usersLoaded.type,
        successMessage: `Users loaded`,
        onError: errorOccurred.type
    });

export const loadUsersMinimalDetails = (userIds) =>
    apiCallBegan({
        url: getUserURL() + 'rest/user/minimalDetails',
        method: 'post',
        data: userIds,
        onSuccess: usersMinimalDetailsLoaded.type,
        onError: errorOccurred.type
    });

export const loadUserTypes = () =>
    apiCallBegan({
        url: getUserURL() + 'rest/user/types',
        onSuccess: userTypesLoaded.type,
        onError: errorOccurred.type
    });

export const loadUserServiceVersion = () =>
    apiVersion({
        url: getUserURL() + 'rest/version',
        method: 'get',
        onSuccess: versionLoaded.type,
        onError: errorOccurred.type
    });

export const saveAccessToken = (accessToken) => {
    localStorage.setItem('accessToken', accessToken);
};

export const setIdToken = (accessToken) => idTokenSet(accessToken);

export const getAccessToken = () => {
    return localStorage.getItem('accessToken');
};

export const mfaRequest = (userId, msg) =>
    apiCallBegan({
        url: getUserURL() + `rest/azure-user/reset-mfa/${userId}`,
        method: 'delete',
        successMessage: msg,
        onError: errorOccurred.type,
        onBusinessLogicError: businessLogicErrorOccurred.type
    });

export const clearLocalStorage = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('currentParticipantId');
    logoutSuccess();
};

export const {
    clearBusinessLogicError,
    clearTeamsForService,
    clearUsersMinimalDetails,
    clearUsersBySearch,
    clearOtherTeamsForService,
    loggedInUserSet,
    userRolesSet
} = slice.actions;

export const { logoutSuccess } = slice.actions;

const {
    businessLogicErrorOccurred,
    errorOccurred,
    otherTeamsForServiceLoaded,
    idTokenSet,
    allTeamsLoaded,
    teamsForLoggedInUserLoaded,
    teamsForServiceLoadingFailure,
    teamsForServiceLoaded,
    teamsForServiceLoading,
    loginSuccess,
    loggedInUserLoaded,
    teamCreated,
    teamLoaded,
    userCreated,
    userUpdated,
    userLoaded,
    userByIdLoaded,
    usersByLoggedInUserServiceIdsLoaded,
    usersByLoggedInUserServiceIdsLoading,
    usersBySearchLoaded,
    userForFormsLoaded,
    usersLoaded,
    usersLoading,
    usersMinimalDetailsLoaded,
    usersForCommsLoaded,
    usersForFormsLoaded,
    usersForVacanciesLoading,
    usersForVacanciesLoaded,
    userTypesLoaded,
    versionLoaded
} = slice.actions;
export default slice.reducer;
